import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fetch } from '@ngrx/router-store/data-persistence';
import { map, tap } from 'rxjs';

import { NotificationService } from '@core/shared/util';
import { isMaintenanceLockError } from '@mp/system/maintenance/data-access';

import { HelpCenterService } from '../services';

import { HelpCenterActions } from './help-center.actions';

/**
 * Der globale HTTP-Interceptor unterdrückt für einen so markierten 503 nur seine EIGENE, generische
 * Fehlermeldung (`HttpErrorClaimHandler`) - dieser Effekt zeigt seine Meldung unabhängig davon selbst an
 * und muss deshalb selbst prüfen. Sonst zeigt die App bei jeder aktiven, nicht befreiten Wartungssperre
 * zusätzlich zur Sperrmeldung noch einen irreführenden "Hilfe Center konnte nicht geladen werden"-Toast,
 * obwohl es sich um exakt dieselbe, bereits angezeigte Sperre handelt.
 */
@Injectable()
export class HelpCenterEffects {
  private readonly actions$ = inject(Actions);
  private readonly service = inject(HelpCenterService);
  private readonly notificationService = inject(NotificationService);

  fetchHelpCenterSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HelpCenterActions.fetchHelpCenterSettings),
      fetch({
        run: () => {
          return this.service
            .getHelpCenterSettings()
            .pipe(
              map((helpCenterSettings) => HelpCenterActions.fetchHelpCenterSettingsSuccess({ helpCenterSettings })),
            );
        },
        onError: (_action, error: unknown) => {
          if (!isMaintenanceLockError(error)) {
            this.notificationService.toastDanger(
              'Beim Laden der Hilfe Center Einstellungen ist ein Fehler aufgetreten.',
            );
          }

          return HelpCenterActions.fetchHelpCenterSettingsError();
        },
      }),
    );
  });

  updateHelpCenterSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HelpCenterActions.updateHelpCenterSettings),
      fetch({
        run: ({ helpCenterSettings }) => {
          return this.service.updateHelpCenterSettings(helpCenterSettings).pipe(
            tap(() => this.notificationService.toastSuccess('Die Hilfe Center Einstellungen wurden aktualisiert.')),
            map((helpCenterSettings) => HelpCenterActions.updateHelpCenterSettingsSuccess({ helpCenterSettings })),
          );
        },
        onError: (_action, error: unknown) => {
          if (!isMaintenanceLockError(error)) {
            this.notificationService.toastDanger(
              'Beim Aktualisieren der Hilfe Center Einstellungen ist ein Fehler aufgetreten.',
            );
          }

          return HelpCenterActions.updateHelpCenterSettingsError();
        },
      }),
    );
  });
}
